import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Button,
  TextField,
  Paper,
  Typography,
  Box,
  Snackbar,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Pagination,
  Grid,
  Card,
  CardContent,
} from '@mui/material';

function ViewAssignment() {
  // State declarations
  const [assignments, setAssignments] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [classes, setClasses] = useState([]);
  const [sections, setSections] = useState([]);
  const [selectedTeacher, setSelectedTeacher] = useState('');
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedSection, setSelectedSection] = useState('');
  const [filteredAssignments, setFilteredAssignments] = useState([]);
  const [editingAssignment, setEditingAssignment] = useState(null);
  const [checkedAssignments, setCheckedAssignments] = useState([]);
  const [assignmentsDropdown, setAssignmentsDropdown] = useState([]);
  const [selectedAssignment, setSelectedAssignment] = useState('');
  const [subjects, setSubjects] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState('');
  const [showAllStudents, setShowAllStudents] = useState(false);
  const [showSubmittedOnly, setShowSubmittedOnly] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const drawerWidth = 240;

  // Helper function to truncate description
  const truncateDescription = (description, wordLimit = 3) => {
    if (typeof description !== 'string') {
      console.warn('Description is not a string:', description);
      return '';
    }
    const words = description.split(' ');
    if (words.length > wordLimit) {
      return words.slice(0, wordLimit).join(' ') + '...';
    }
    return description;
  };

  // Pagination logic
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const paginatedAssignments = filteredAssignments.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  // Add this useEffect after your existing useEffects
useEffect(() => {
  // Reset pagination and selections when any filter changes
  setPage(1);
  setCheckedAssignments([]);
  setFilteredAssignments([]);
  setAssignments([]);
}, [selectedTeacher, selectedClass, selectedSection, selectedSubject, selectedAssignment]);

  // Fetch teachers on component mount
  useEffect(() => {
    fetchTeachers();
  }, []);

  // Fetch classes when teacher is selected
  useEffect(() => {
    if (selectedTeacher) {
      fetchClasses(selectedTeacher);
    } else {
      setClasses([]);
      setSections([]);
      setSubjects([]);
      setAssignmentsDropdown([]);
       setSelectedClass('');      // Add this
    setSelectedSection('');    // Add this
    setSelectedSubject('');    // Add this
    setSelectedAssignment(''); // Add this
    }
  }, [selectedTeacher]);

  // Fetch sections when class is selected
  useEffect(() => {
    if (selectedClass) {
      fetchSections(selectedClass);
    } else {
      setSections([]);
      setSubjects([]);
      setAssignmentsDropdown([]);
       setSelectedSection('');    // Add this
    setSelectedSubject('');    // Add this
    setSelectedAssignment(''); // Add this
    }
  }, [selectedClass]);

  // Fetch subjects when section is selected
  useEffect(() => {
    if (selectedSection) {
      fetchSubjects(selectedClass, selectedSection, selectedTeacher);
    } else {
      setSubjects([]);
      setAssignmentsDropdown([]);
       setSelectedSubject('');    // Add this
    setSelectedAssignment(''); // Add this
    }
  }, [selectedSection]);

  // Fetch assignments dropdown when class, section, and subject are selected
  useEffect(() => {
    if (selectedClass && selectedSection && selectedSubject) {
      fetchAssignmentsDropdown(selectedClass, selectedSection, selectedSubject);
    } else {
      setAssignmentsDropdown([]);
    }
  }, [selectedClass, selectedSection, selectedSubject]);

  // Fetch assignments when assignment is selected
  useEffect(() => {
    if (selectedAssignment) {
      fetchAssignments();
    }
  }, [selectedAssignment]);

  // Fetch teachers
  const fetchTeachers = async () => {
    try {
      const response = await fetch('https://wsmsv1.aidigitalsec.com/api/Teacher/GetTeacher', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: sessionStorage.getItem('token'),
        },
      });
      const data = await response.json();
      console.log('Teachers API Response:', data);
      let teacherData = [];
      if (Array.isArray(data)) {
        teacherData = data;
      } else if (data && Array.isArray(data.teachers)) {
        teacherData = data.teachers;
      }
      setTeachers(teacherData);
      return teacherData;
    } catch (error) {
      console.error('Error fetching teachers:', error);
      setTeachers([]);
      throw error;
    }
  };

  // Fetch classes
  const fetchClasses = async (teacherId) => {
    try {
      const response = await fetch('https://wsmsv1.aidigitalsec.com/api/Teacher/GetClass', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: sessionStorage.getItem('token'),
        },
        body: JSON.stringify({ teacherId }),
      });
      const data = await response.json();
      if (data.data === null && data.msg === 'Record Not Found') {
        setClasses([]);
        setSnackbarMessage('Data not found for Classes');
        setSnackbarOpen(true);
      } else if (Array.isArray(data)) {
        if (data.length === 0) {
          setClasses([]);
          setSnackbarMessage('Data not found for Classes');
          setSnackbarOpen(true);
        } else {
          setClasses(data);
        }
      } else {
        setClasses([]);
        setSnackbarMessage('Data not found for Classes');
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.error('Error fetching classes:', error);
      setClasses([]);
      setSnackbarMessage('Data not found for Classes');
      setSnackbarOpen(true);
    }
  };

  // Fetch sections
  const fetchSections = async (classId) => {
    try {
      const response = await fetch('https://wsmsv1.aidigitalsec.com/api/Teacher/ddlSection_clsId', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: sessionStorage.getItem('token'),
        },
        body: JSON.stringify({ teacherId: selectedTeacher, classId }),
      });
      const data = await response.json();
      if (Array.isArray(data) && data.length === 0) {
        setSections([]);
        setSnackbarMessage('Data not found for Sections');
        setSnackbarOpen(true);
      } else {
        setSections(data);
      }
    } catch (error) {
      console.error('Error fetching sections:', error);
      setSections([]);
      setSnackbarMessage('Data not found for Sections');
      setSnackbarOpen(true);
    }
  };

  // Fetch subjects
  const fetchSubjects = async (classId, sectionId, teacherId) => {
    try {
      const response = await fetch('https://wsmsv1.aidigitalsec.com/api/AssignmentCreate/Subjectddl', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: sessionStorage.getItem('token'),
        },
        body: JSON.stringify({ classId, sectionId, teacherId }),
      });
      const data = await response.json();
      console.log('Subjects:', data);
      if (Array.isArray(data) && data.length === 0) {
        setSubjects([]);
        setSnackbarMessage('Data not found for Subjects');
        setSnackbarOpen(true);
      } else {
        setSubjects(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error('Error fetching subjects:', error);
      setSubjects([]);
      setSnackbarMessage('Data not found for Subjects');
      setSnackbarOpen(true);
    }
  };

  // Fetch assignments dropdown
  const fetchAssignmentsDropdown = async (classId, sectionId, subjectId) => {
    try {
      const response = await fetch(
        'https://wsmsv1.aidigitalsec.com/api/AssignmentCreate/GetAssignmentbySubId',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: sessionStorage.getItem('token'),
          },
          body: JSON.stringify({ classId, sectionId, subjectId }),
        }
      );
      const data = await response.json();
      console.log('Assignments Dropdown Data:', data);
      const filteredData = Array.isArray(data) ? data : [];
      if (filteredData.length === 0) {
        setAssignmentsDropdown([]);
        setSnackbarMessage('Data not found for Assignments');
        setSnackbarOpen(true);
      } else {
        setAssignmentsDropdown(filteredData);
      }
    } catch (error) {
      console.error('Error fetching assignments:', error);
      setAssignmentsDropdown([]);
      setSnackbarMessage('Data not found for Assignments');
      setSnackbarOpen(true);
    }
  };

  // Fetch assignments
  const fetchAssignments = async () => {
    if (!selectedClass || !selectedSection || !selectedTeacher || !selectedAssignment) {
      console.error('All required fields are not selected.');
      return;
    }

    try {
      const response = await fetch('https://wsmsv1.aidigitalsec.com/api/Teacher/GetAssignmentView', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: sessionStorage.getItem('token'),
        },
        body: JSON.stringify({
          classId: selectedClass,
          sectionId: selectedSection,
          teacherId: selectedTeacher,
          assignmentId: selectedAssignment,
        }),
      });
      const data = await response.json();
      if (Array.isArray(data) && data.length === 0) {
        setAssignments([]);
        setFilteredAssignments([]);
        setSnackbarMessage('Data not found for Assignments');
        setSnackbarOpen(true);
      } else {
        setAssignments(data);
        setFilteredAssignments(data);
      }
    } catch (error) {
      console.error('Error fetching assignments:', error);
      setAssignments([]);
      setFilteredAssignments([]);
      setSnackbarMessage('Data not found for Assignments');
      setSnackbarOpen(true);
    }
  };

  // Show all students
  const handleShowAllStudents = () => {
    setShowAllStudents(true);
    setShowSubmittedOnly(false);
    setFilteredAssignments(assignments);
  };

  // Show submitted only
  const handleShowSubmittedOnly = () => {
    setShowAllStudents(false);
    setShowSubmittedOnly(true);
    const submittedOnly = assignments.filter(
      (assignment) => assignment.submitedFile && assignment.submitedFile.trim() !== ""
    );
    setFilteredAssignments(submittedOnly);
  };

  // Handle description change
  const handleDescriptionChange = (e, id) => {
    const { value } = e.target;
    setAssignments((prev) =>
      prev.map((assignment) =>
        assignment.studentId === id ? { ...assignment, description: value } : assignment
      )
    );
    setFilteredAssignments((prev) =>
      prev.map((assignment) =>
        assignment.studentId === id ? { ...assignment, description: value } : assignment
      )
    );
  };

  // View file
  const handleViewFile = (fileUrl) => {
    window.open(`https://wsmsv1.aidigitalsec.com/${fileUrl}`, '_blank');
  };

  // Handle save
  const handleSave = async () => {
    const payload = checkedAssignments.map((assignment) => ({
      assignmentId: assignment.assignmentId,
      studentId: assignment.studentId,
      description: assignment.description,
      isStudentChecked: true,
    }));

    try {
      const response = await fetch('https://wsmsv1.aidigitalsec.com/api/Teacher/SaveAssignmentDescription', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: sessionStorage.getItem('token'),
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        setSnackbarMessage('Assignments saved successfully');
        setSnackbarOpen(true);
      } else {
        setSnackbarMessage('Error saving assignments');
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.error('Error saving assignments:', error);
      setSnackbarMessage('Error saving assignments');
      setSnackbarOpen(true);
    }
  };

  // Handle cancel
  const handleCancel = () => {
    setCheckedAssignments([]);
    setSelectedTeacher('');
    setSelectedClass('');
    setSelectedSection('');
    setSelectedSubject('');
    setSelectedAssignment('');
  };

  // Handle checkbox change
  const handleCheckboxChange = (assignment) => {
    setCheckedAssignments((prev) => {
      if (prev.some((a) => a.studentId === assignment.studentId)) {
        return prev.filter((a) => a.studentId !== assignment.studentId);
      } else {
        return [...prev, assignment];
      }
    });
  };

  // Handle snackbar close
  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  return (
   <Box
  component="main"
  sx={{
    width: { md: `calc(100% - ${drawerWidth}px)` },
    ml: { md: `${drawerWidth}px` },
    mt: { xs: 0, sm: '64px' },
    p: 3,
    backgroundColor: '#f4f6f9', // ✅ screenshot match
    minHeight: '100vh',
  }}
>


      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        message={snackbarMessage}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{
          '& .MuiSnackbarContent-root': {
            backgroundColor: snackbarMessage.includes('Error') ? '#d32f2f' : '#4caf50',
            color: '#fff',
            fontWeight: 'bold',
          },
        }}
      />

      <Card
  sx={{
    mb: 4,
    borderRadius: 3,
    boxShadow: '0 8px 25px rgba(0,0,0,0.08)',
  }}
>
  <CardContent sx={{ p: 4 }}>

          <Typography
  variant="h4"
  align="center"
  sx={{
    mb: 4,
    fontWeight: 700,
    color: '#d97706'
,
  }}
>
  VIEW ASSIGNMENTS

</Typography>


<Grid container spacing={3} sx={{ mb: 4 }}>

            <Grid item xs={12} md={2.4}>
              <FormControl
  fullWidth
  variant="outlined"
  sx={{ backgroundColor: '#fff', borderRadius: 1 }}
>
                <InputLabel>Select Teacher</InputLabel>
                <Select
                  value={selectedTeacher}
                  onChange={(e) => setSelectedTeacher(e.target.value)}
                  label="Select Teacher"
                  sx={{ backgroundColor: 'white' }}
                >
                  <MenuItem value="">Select Teacher</MenuItem>
                  {teachers.map((teacher) => (
                    <MenuItem key={teacher.employeeId} value={teacher.employeeId}>
                      {teacher.employeeName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={2.4}>
             <FormControl
  fullWidth
  variant="outlined"
  sx={{ backgroundColor: '#fff', borderRadius: 1 }}
>

                <InputLabel>Select Class</InputLabel>
                <Select
                  value={selectedClass}
                  onChange={(e) => setSelectedClass(e.target.value)}
                  label="Select Class"
                  sx={{ backgroundColor: 'white' }}
                >
                  <MenuItem value="">Select Class</MenuItem>
                  {classes.map((cls) => (
                    <MenuItem key={cls.classId} value={cls.classId}>
                      {cls.className}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={2.4}>
              <FormControl fullWidth variant="outlined" disabled={!selectedClass}>
                <InputLabel>Select Section</InputLabel>
                <Select
                  value={selectedSection}
                  onChange={(e) => setSelectedSection(e.target.value)}
                  label="Select Section"
                  sx={{ backgroundColor: 'white' }}
                >
                  <MenuItem value="">Select Section</MenuItem>
                  {sections.map((sec) => (
                    <MenuItem key={sec.sectionId} value={sec.sectionId}>
                      {sec.sectionName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={2.4}>
              <FormControl fullWidth variant="outlined" disabled={!selectedSection}>
                <InputLabel>Select Subject</InputLabel>
                <Select
                  value={selectedSubject}
                  onChange={(e) => setSelectedSubject(e.target.value)}
                  label="Select Subject"
                  sx={{ backgroundColor: 'white' }}
                >
                  <MenuItem value="">Select Subject</MenuItem>
                  {subjects.map((sub) => (
                    <MenuItem key={sub.subjectId} value={sub.subjectId}>
                      {sub.subjectName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={2.4}>
              <FormControl fullWidth variant="outlined" disabled={!selectedSubject}>
                <InputLabel>Select Assignment</InputLabel>
                <Select
                  value={selectedAssignment}
                  onChange={(e) => setSelectedAssignment(e.target.value)}
                  label="Select Assignment"
                  sx={{ backgroundColor: 'white' }}
                >
                  <MenuItem value="">Select Assignment</MenuItem>
                  {assignmentsDropdown.map((assignment) => (
                    <MenuItem key={assignment.assignmentId} value={assignment.assignmentId}>
                      {assignment.assignmentName || assignment.assignmentDescription}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
           <Button
  variant="contained"
  onClick={handleShowAllStudents}
  sx={{
    px: 4,
    backgroundColor: '#2e7d32',
    fontWeight: 600,
    '&:hover': { backgroundColor: '#1b5e20' },
  }}
>
  Show All Students
</Button>

           <Button
  variant="contained"
  onClick={handleShowSubmittedOnly}
  sx={{
    px: 4,
    backgroundColor: '#0288d1',
    fontWeight: 600,
    '&:hover': { backgroundColor: '#01579b' },
  }}
>
  Show Submitted Only
</Button>

          </Box>
        </CardContent>
      </Card>
<Card
  sx={{
    borderRadius: 3,
    boxShadow: '0 8px 25px rgba(0,0,0,0.08)',
  }}
>
  <CardContent sx={{ p: 0 }}>

          <TableContainer component={Paper} sx={{ boxShadow: 'none' }}>
            <Table>
<TableHead
  sx={{
    backgroundColor: '#d97706', // 🟠 navbar orange
    '& th': {
      color: '#ffffff',         // 🤍 white text
      fontWeight: 700,
      fontSize: '0.95rem',
    },
  }}
>


                <TableRow>
                 <TableCell sx={{ color: '#d97706', fontWeight: 700 }}>
  Select
</TableCell>
<TableCell sx={{ color: '#d97706', fontWeight: 700 }}>
  Admission No
</TableCell>
<TableCell sx={{ color: '#d97706', fontWeight: 700 }}>
  Student
</TableCell>
<TableCell sx={{ color: '#d97706', fontWeight: 700 }}>
  Class
</TableCell>
<TableCell sx={{ color: '#d97706', fontWeight: 700 }}>
  Section
</TableCell>
<TableCell sx={{ color: '#d97706', fontWeight: 700 }}>
  Roll
</TableCell>
<TableCell sx={{ color: '#d97706', fontWeight: 700 }}>
  Submitted File
</TableCell>
<TableCell sx={{ color: '#d97706', fontWeight: 700 }}>
  Remarks
</TableCell>


                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedAssignments.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      <Typography sx={{ py: 3, color: '#666' }}>No Data Available</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedAssignments.map((assignment) => (
                    <TableRow key={assignment.studentId} hover>
                      <TableCell>
                        <Checkbox
                          checked={checkedAssignments.some((a) => a.studentId === assignment.studentId)}
                          onChange={() => handleCheckboxChange(assignment)}
                          color="primary"
                          disabled={!assignment.submitedFile} // Disable if no file submitted
                        />
                      </TableCell>
                      <TableCell>{assignment.admissionNo || 'N/A'}</TableCell>
                      <TableCell>{assignment.studentName || 'N/A'}</TableCell>
                      {/* <TableCell>{assignment.subjectName || 'N/A'}</TableCell> */}
                      <TableCell>{assignment.className || 'N/A'}</TableCell>
                      <TableCell>{assignment.sectionName || 'N/A'}</TableCell>
                      <TableCell>{assignment.rollNo || 'N/A'}</TableCell>
                      <TableCell>
                        {assignment.submitedFile ? (
                          <Button
                            variant="contained"
                            size="small"
                            onClick={() => handleViewFile(assignment.submitedFile)}
                            sx={{ backgroundColor: '#1976d2' }}
                          >
                            View
                          </Button>
                        ) : (
                          <Button variant="outlined" size="small" disabled>
                            NOT SUBMITTED
                          </Button>
                        )}
                      </TableCell>
                      <TableCell>
                        <TextField
                          value={assignment.description || ''}
                          onChange={(e) => handleDescriptionChange(e, assignment.studentId)}
                          size="small"
                          variant="outlined"
                          fullWidth
                           disabled={!assignment.submitedFile} // Disable if no file submitted
                        />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
<Box sx={{ 
  display: 'flex', 
  justifyContent: 'space-between', 
  alignItems: 'center',
  mt: 3,
  pt: 2,
  borderTop: '1px solid #e0e0e0'
}}>
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
    <Typography variant="body2" sx={{ color: '#666' }}>
      Rows per page
    </Typography>
    <FormControl size="small" sx={{ minWidth: 80 }}>
      <Select
        value={rowsPerPage}
        onChange={(e) => {
          setRowsPerPage(e.target.value);
          setPage(1);
        }}
        sx={{ backgroundColor: 'white' }}
      >
        <MenuItem value={5}>5</MenuItem>
        <MenuItem value={10}>10</MenuItem>
        <MenuItem value={25}>25</MenuItem>
        <MenuItem value={50}>50</MenuItem>
      </Select>
    </FormControl>
  </Box>
  
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
    <Typography variant="body2" sx={{ color: '#666' }}>
      Page {page} of {Math.ceil(filteredAssignments.length / rowsPerPage) || 1}
    </Typography>
    
    <Box sx={{ display: 'flex', gap: 1 }}>
      <Button
        variant="outlined"
        onClick={() => setPage(page - 1)}
        disabled={page === 1}
        sx={{ 
          minWidth: '80px',
          color: '#666',
          borderColor: '#ddd',
          '&:hover': { borderColor: '#999' }
        }}
      >
        PREV
      </Button>
      <Button
        variant="outlined"
        onClick={() => setPage(page + 1)}
        disabled={page >= Math.ceil(filteredAssignments.length / rowsPerPage)}
        sx={{ 
          minWidth: '80px',
          color: '#1976d2',
          borderColor: '#1976d2',
          '&:hover': { backgroundColor: '#f0f7ff' }
        }}
      >
        NEXT
      </Button>
    </Box>
  </Box>
</Box>
        </CardContent>
      </Card>

      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 3 }}>
        <Button
          variant="contained"
          onClick={handleSave}
          sx={{ backgroundColor: '#2e7d32', '&:hover': { backgroundColor: '#388e3c' } }}
        >
          Save
        </Button>
        <Button
          variant="contained"
          onClick={handleCancel}
          sx={{ backgroundColor: '#d32f2f', '&:hover': { backgroundColor: '#d32f2f' } }}
        >
          Reset
        </Button>
      </Box>
    </Box>
  );
}

export default ViewAssignment;
